/**
 * 경량 모달 유틸 (관리자) — 이제 공용 엔진 $j.Ui.Modal 위의 얇은 어댑터.
 *
 * 공개 API(하위호환 유지):
 *   $j.Ui.JBModal.open('my-modal', '/page.php?n=1', null, {title:'제목'});   // URL 로드
 *   $j.Ui.JBModal.html('my-modal', '<p>내용</p>', '제목', {width:'720px'});   // HTML
 *   $j.Ui.JBModal.target('my-modal', {target:'$j.F.X', data:{}, title:'..'}); // $j 모듈 실행
 *   $j.Ui.JBModal.close('my-modal');
 *
 * 옵션: title / width / fullScreen(→mode:full) / position:'right'(→mode:right) / method / onOpen($modal)
 *
 * 하위호환: 관리자 코드가 `.jb-modal-body` `.jb-modal-title` 등 클래스를 직접 참조하므로
 *   엔진의 ui-modal__* 요소에 해당 호환 클래스를 부여한다. 룩은 variant:'admin' 테마로 보존.
 *   스택 z-index 는 공용 $j.Ui.Layer 를 통하므로 BS·Ui.Modal 과 한 스택으로 겹친다.
 */
export default class JBModal {
    constructor() {
        this.lastId = null;
        this._targets = {}; // id → $j 모듈 인스턴스(target 모드)
        this._handles = {}; // id → Modal 핸들
    }

    destroy() {
        $j.Ui.Div.destroyByContainer(this);
        $j.Common.EventBus.offNamespace($j.getNS(this));
    }

    _mode(opts = {}) {
        if (opts.fullScreen) return 'full';
        if (opts.position === 'right') return 'right';
        return 'center';
    }

    // 관리자 코드가 참조하는 .jb-modal-* 호환 클래스 부여
    _decorate(handle) {
        handle.el.addClass('jb-modal');
        handle.el.find('.ui-modal__header').addClass('jb-modal-header');
        handle.el.find('.ui-modal__title').addClass('jb-modal-title');
        handle.el.find('.ui-modal__close').addClass('jb-modal-close');
        handle.el.find('.ui-modal__body').addClass('jb-modal-body');
    }

    _openShell(id, opts, bodyHtml) {
        const handle = $j.Ui.Modal.open({
            id,
            mode: this._mode(opts),
            title: opts.title || '',
            variant: 'admin',
            width: opts.width,
            html: bodyHtml != null ? bodyHtml : '',
            dismissible: true,
            onClose: () => this._afterClose(id),
        });
        this._decorate(handle);
        this._handles[id] = handle;
        this.lastId = id;
        if (opts.onOpen) opts.onOpen(handle.el);
        return handle;
    }

    /** HTML 문자열로 모달을 연다. */
    html = (id, html, title = '', opts = {}) => {
        this._openShell(id, Object.assign({}, opts, { title }), html);
    }

    /** URL 로 내용을 불러와서 모달을 연다. (반환 HTML 에서 <body>·<style> 추출) */
    open = (id, url, param = null, opts = {}) => {
        const self = this;
        $.ajax({
            type: opts.method || 'GET',
            url,
            data: param,
            success(html) { self._openShell(id, opts, self._extractBody(html)); },
        });
    }

    /** $j 모듈(target)을 모달 안에서 실행한다. */
    target = (id, option = {}) => {
        const bodyId = id + '-body';
        if (this._targets[id]) { this._targets[id].destroy?.(); delete this._targets[id]; } // 이전 target 먼저 정리
        const handle = this._openShell(id, option, '');
        handle.el.find('.ui-modal__body').attr('id', bodyId).empty();

        const target = $j.reset(option.target);
        target.id = bodyId;
        target.data = option.data || {};
        target.parent = this;
        this._targets[id] = target;
        target.init();
    }

    /** 모달을 닫는다. */
    close = (id) => {
        const targetId = id || this.lastId;
        if (!targetId) return;
        const h = this._handles[targetId];
        if (h) h.close();                 // → Modal onClose → _afterClose
        else $j.Ui.Modal.close(targetId);
    }

    // Modal 닫힘(버튼/백드롭/close 공통) 후처리 — target 정리 + lastId 갱신 + EventBus
    _afterClose = (id) => {
        if (this._targets[id]) { this._targets[id].destroy?.(); delete this._targets[id]; }
        delete this._handles[id];
        this.lastId = $j.Ui.Layer.top();
        $j.Common.EventBus.trigger('JBModal:close', { id });
    }

    // 반환 HTML 이 전체 문서면 <body> 안쪽 + <style> 만 추출 (popup 페이지 로드용)
    _extractBody(html) {
        const m = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        if (!m) return html;
        let body = m[1];
        const styles = [];
        html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, function (tag) { styles.push(tag); return ''; });
        return styles.join('') + body;
    }
}
